import React from 'react';
import { motion } from 'framer-motion';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import CustomerLogos from '../../components/CustomerLogos/CustomerLogos';
import useSimpleCounter from '../../hooks/useSimpleCounter';
import clientsHeroImage from '../../images/AboutUs/AboutUs.jpg';
import './OurClients.css';

const AchievementCard = ({ item, index }) => {
  const [animatedValue, elementRef] = useSimpleCounter(item.number, 2000, index * 150);

  return (
    <motion.div
      ref={elementRef}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="our-clients-achievement-card"
    >
      <div className="our-clients-achievement-number">{animatedValue || '0'}</div>
      <div className="our-clients-achievement-label">{item.label}</div>
    </motion.div>
  );
};

const OurClients = () => {
  const achievements = [
    { number: '500+', label: 'Happy Clients' },
    { number: '25+', label: 'Years of Experience' },
    { number: '1000+', label: 'Projects Completed' }
  ];

  const industryClients = [
    {
      industry: 'Cement Industries',
      type: 'table',
      headers: ['Ultratech Cement Ltd Units', '', 'Other Cement'],
      rows: [
        ['Vikram Cement Works', 'Bela Cement Works', 'ACC, Kaymor'],
        ['Sewagram Cement Works', 'Siddhi Cement Works', 'KJS Cement, Maihar'],
        ['Aditya Cement Works', 'Mahiar Cement Works', 'Sindhi Cement Works Veraval'],
        ['Bela Cement Works', 'Balaji Cement Works', 'Nimbol Cement Works (NUVOCO)'],
        ['Kotputli Cement Works', 'AP Cement Works', 'Jojobera Cement Works (NUVOCO)'],
        ['Nathdwarra Cement Works', 'Rajashree Cement Works', ''],
        ['Dalla Cement Works', 'Awarpur Cement Works', ''],
        ['Gujarat Cement Works', 'Baikunth Cement Works', ''],
        ['Rawan Cement Works', 'Dhar Cement Works', ''],
        ['Hirmi Cement Works', 'Birla white, Natdhawara', '']
      ]
    },
    {
      industry: 'Power',
      type: 'list',
      columns: 2,
      clients: [
        'JSW'
      ]
    },
    {
      industry: 'Pulp and Paper, Chemical',
      type: 'list',
      columns: 2,
      clients: [
        'Hindalco Industries Ltd, Hirakud',
        'Westrock India Pvt. Ltd., Vapi, GJ',
        'Grasim Industries Ltd, Vilayat, GJ',
        'Otsuka India Pvt. Ltd., Kotputli, RJ',
        'Bunge India Pvt. Ltd., Bacchau, GJ'
      ]
    },
    {
      industry: 'Oil & Gas Refineries',
      type: 'list',
      columns: 2,
      clients: [
        'BPCL, Mahul Refinery',
        'HPCL, Mahul Refinery',
        'Ruwais Refinery, Abu Dhabi'
      ]
    },
    {
      industry: 'Sugar Industry',
      type: 'list',
      columns: 2,
      clients: []
    }
  ];

  const splitIntoColumns = (items, columns) => {
    const safeCols = Math.max(1, columns || 1);
    const colArrays = Array.from({ length: safeCols }, () => []);
    items.forEach((item, i) => {
      colArrays[i % safeCols].push(item);
    });
    return colArrays;
  };

  return (
    <div className="our-clients-page">
      <section className="our-clients-hero" style={{ backgroundImage: `url(${clientsHeroImage})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="our-clients-hero-content"
          >
            <Breadcrumb />
            <h1 className="our-clients-hero-title">
              Our <span className="our-clients-hero-title-highlight">Clients</span>
            </h1>
            <p className="our-clients-hero-description">
              We are proud to support leading organizations across industries with reliable valve, actuator,
              pump, and workshop services.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="our-clients-logos-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="our-clients-logos-header"
          >
            <h2 className="our-clients-logos-title">Trusted By Industry Leaders</h2>
            <p className="our-clients-logos-description">
              Our long-term customer relationships reflect our focus on quality, fast response, and dependable service.
            </p>
          </motion.div>

          <CustomerLogos />
        </div>
      </section>

      <section className="our-clients-achievements">
        <div className="container-custom">
          <div className="our-clients-achievements-grid">
            {achievements.map((item, index) => (
              <AchievementCard key={item.label} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="our-clients-industry-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="our-clients-industry-header"
          >
            <h2 className="our-clients-industry-title">Client Industries</h2>
            <p className="our-clients-industry-description">
              Industry-wise list of organizations we support.
            </p>
          </motion.div>

          <div className="our-clients-industry-accordion">
            {industryClients.map((group, idx) => (
              <motion.details
                key={group.industry}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: idx * 0.06 }}
                className="our-clients-industry-panel"
                open={idx === 0}
              >
                <summary className="our-clients-industry-summary">
                  <span className="our-clients-industry-name">{group.industry}</span>
                  <span className="our-clients-industry-chevron" aria-hidden="true" />
                </summary>

                <div className="our-clients-industry-body">
                  {group.type === 'table' ? (
                    <div className="our-clients-table-wrap" role="region" aria-label={`${group.industry} clients`}>
                      <table className="our-clients-table">
                        <thead>
                          <tr>
                            <th colSpan={2}>{group.headers[0]}</th>
                            <th>{group.headers[2]}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row, rIdx) => (
                            <tr key={rIdx}>
                              {row.map((cell, cIdx) => (
                                <td key={cIdx}>{cell || '\u00A0'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    group.clients.length > 0 && (
                      <div
                        className="our-clients-simple-table"
                        style={{ gridTemplateColumns: `repeat(${Math.max(1, group.columns)}, minmax(0, 1fr))` }}
                      >
                        {splitIntoColumns(group.clients, group.columns).map((col, cIdx) => (
                          <ul key={cIdx} className="our-clients-simple-col">
                            {col.map((client) => (
                              <li key={client} className="our-clients-simple-row">
                                {client}
                              </li>
                            ))}
                          </ul>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurClients;
